/**
 * Fonte única da preferência de Analytics (opt-in).
 *
 * Independente do aceite de Termos/Política e do opt-in de e-mail marketing.
 * Não armazena UID, e-mail, nome ou identificadores de projeto.
 */

export const PRIVACY_CONSENT_STORAGE_KEY = "fivi_privacy_consent_v1";
export const PRIVACY_CONSENT_VERSION = 1;
export const PRIVACY_PREFERENCES_EVENT = "fivi:open-privacy-preferences";

/** @typedef {{ version: number, analytics: boolean, updatedAt: string }} PrivacyConsentState */

const listeners = new Set();

/**
 * @returns {boolean}
 */
function canUseStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

/**
 * @param {unknown} value
 * @returns {PrivacyConsentState | null}
 */
export function parsePrivacyConsentState(value) {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = /** @type {Record<string, unknown>} */ (value);

  if (record.version !== PRIVACY_CONSENT_VERSION) {
    return null;
  }

  if (typeof record.analytics !== "boolean") {
    return null;
  }

  if (typeof record.updatedAt !== "string" || !record.updatedAt.trim()) {
    return null;
  }

  return {
    version: PRIVACY_CONSENT_VERSION,
    analytics: record.analytics,
    updatedAt: record.updatedAt,
  };
}

/**
 * @returns {PrivacyConsentState | null}
 */
export function getPrivacyConsentState() {
  if (!canUseStorage()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(PRIVACY_CONSENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return parsePrivacyConsentState(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * Ausência de escolha nunca é aceite.
 *
 * @returns {boolean}
 */
export function hasAnalyticsConsent() {
  return getPrivacyConsentState()?.analytics === true;
}

/**
 * @returns {boolean}
 */
export function hasAnsweredPrivacyConsent() {
  return getPrivacyConsentState() != null;
}

/**
 * @param {PrivacyConsentState | null} state
 */
function emit(state) {
  listeners.forEach((listener) => {
    try {
      listener(state);
    } catch {
      // Listeners não podem quebrar o fluxo de consentimento.
    }
  });
}

/**
 * @param {boolean} enabled
 * @returns {PrivacyConsentState}
 */
export function setAnalyticsConsent(enabled) {
  const state = {
    version: PRIVACY_CONSENT_VERSION,
    analytics: Boolean(enabled),
    updatedAt: new Date().toISOString(),
  };

  if (canUseStorage()) {
    window.localStorage.setItem(
      PRIVACY_CONSENT_STORAGE_KEY,
      JSON.stringify(state),
    );
  }

  emit(state);
  return state;
}

/**
 * @param {(state: PrivacyConsentState | null) => void} listener
 * @returns {() => void}
 */
export function subscribePrivacyConsent(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function openPrivacyPreferences() {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent(PRIVACY_PREFERENCES_EVENT));
}

export function __resetPrivacyConsentForTests() {
  listeners.clear();
  if (canUseStorage()) {
    window.localStorage.removeItem(PRIVACY_CONSENT_STORAGE_KEY);
  }
}
